import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Filter, Download, Search, RefreshCw, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { platformApi, Transaction } from '@/services/platformApi';
import { 
  deduplicateTransactions, 
  isValidTransaction, 
  sortTransactionsChronologically,
  shouldShowSeparateSections,
  filterBalanceOperations,
  filterTokenTransactions
} from '@/utils/transactionUtils';
import TransactionListContainer from '@/components/transactions/TransactionListContainer';
import TransactionFilters from '@/components/transactions/TransactionFilters';
import TransactionExport from '@/components/transactions/TransactionExport';
import { i18n } from '@/utils/i18n';

interface TransactionFilters {
  type?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

interface TransactionHistoryPageProps {
  embedded?: boolean; // When true, hides header/navigation and shows as embedded component
  hideFilters?: boolean; // When true, hides filter UI
  limit?: number; // Custom limit for number of transactions
  showPagination?: boolean; // When false, hides pagination
  sortMode?: 'chronological' | 'grouped'; // New prop for sorting mode
  onSeeAll?: () => void; // Callback for "See All" action in embedded mode
  shouldFetch?: boolean; // When false, prevents automatic data fetching (useful for collapsible sections)
  autoOpenDetails?: boolean; // When true (e.g. /transactions/detail/:code), open matching row details automatically
}

const matchesCode = (tx: Transaction, code: string): boolean => {
  const normalized = code.trim().toLowerCase();
  if (!normalized) return true;
  const candidates = [tx.tokenKey, tx.key, (tx as { code?: string }).code, tx.id, tx._id]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());
  return candidates.includes(normalized);
};

const filterByCode = (txs: Transaction[], code: string | undefined): Transaction[] => {
  if (!code?.trim()) return txs;
  return txs.filter((tx) => matchesCode(tx, code));
};

const TransactionHistoryPage: React.FC<TransactionHistoryPageProps> = ({ 
  embedded = false,
  hideFilters = false,
  limit = 20,
  showPagination = true,
  sortMode = 'chronological', // Default to chronological sorting
  onSeeAll,
  shouldFetch = true, // Default to true for backward compatibility
  autoOpenDetails = false,
}) => {
  const navigate = useNavigate();
  const { code: routeCode } = useParams<{ code?: string }>();
  const [searchParams] = useSearchParams();
  const codeFilter = useMemo(() => {
    if (embedded) return '';
    const fromPath = routeCode?.trim();
    if (fromPath) return fromPath;
    return searchParams.get('code')?.trim() ?? '';
  }, [embedded, routeCode, searchParams]);
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);
  const [completedTransactions, setCompletedTransactions] = useState<Transaction[]>([]);
  const [balanceOperations, setBalanceOperations] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const itemsPerPage = limit;
  
  // Filters and Search
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Selected transaction for inline details
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  
  // Export dialog
  const [showExport, setShowExport] = useState(false);
  const autoOpenedCodeRef = useRef<string | null>(null);

  // Sync search input with route :code
  useEffect(() => {
    if (embedded) return;
    if (codeFilter) {
      setSearchTerm(codeFilter);
      setCurrentPage(1);
      if (!autoOpenDetails) {
        setSelectedTransaction(null);
      }
    }
  }, [codeFilter, embedded, autoOpenDetails]);

  // /transactions/detail/:code — auto-open details for the matching row
  useEffect(() => {
    if (!autoOpenDetails || !codeFilter || loading) return;
    if (autoOpenedCodeRef.current === codeFilter) return;

    const combined = deduplicateTransactions([
      ...pendingTransactions,
      ...completedTransactions,
      ...transactions,
    ]);
    const match = combined.find((tx) => matchesCode(tx, codeFilter));
    if (match) {
      setSelectedTransaction(match);
      autoOpenedCodeRef.current = codeFilter;
    }
  }, [
    autoOpenDetails,
    codeFilter,
    loading,
    pendingTransactions,
    completedTransactions,
    transactions,
  ]);

  useEffect(() => {
    autoOpenedCodeRef.current = null;
  }, [codeFilter, autoOpenDetails]);

  useEffect(() => {
    if (!user) {
      return;
    }
    // Only fetch transactions if shouldFetch is true (allows parent to control when to fetch)
    if (shouldFetch) {
      fetchTransactions();
    }
  }, [user, navigate, currentPage, filters, shouldFetch, codeFilter]);

  const fetchTransactions = async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const effectiveSearch = codeFilter || searchTerm.trim() || undefined;
      
            const response = await platformApi.getTransactions({
        page: showPagination ? currentPage : 1,
        limit: embedded ? undefined : itemsPerPage, // For embedded mode, get all transactions to apply our own limits
        ...filters,
        search: effectiveSearch,
      });
      
      // Use utility functions for transaction processing
      let pendingTransactions = deduplicateTransactions(response.pending || []);
      let completedTransactions = deduplicateTransactions(response.completed || []);
      
      // Filter out malformed transactions
      pendingTransactions = pendingTransactions.filter(isValidTransaction);
      completedTransactions = completedTransactions.filter(isValidTransaction);

      // Exact match on token code when /transactions/:code is active
      if (codeFilter) {
        pendingTransactions = filterByCode(pendingTransactions, codeFilter);
        completedTransactions = filterByCode(completedTransactions, codeFilter);
      }
      
      // Separate balance operations (balance_up and balance_down) from token transactions
      const allTransactions = [...pendingTransactions, ...completedTransactions];
      const balanceOps = filterBalanceOperations(allTransactions);
      const tokenTransactions = filterTokenTransactions(allTransactions);
      
      // Filter token transactions back into pending/completed
      const pendingTokens = filterTokenTransactions(pendingTransactions);
      const completedTokens = filterTokenTransactions(completedTransactions);
      
      // For embedded mode (RecentActivity), get the 20 most recent transactions overall
      if (embedded && limit) {
        // Combine ALL transactions (including balance ops) and sort chronologically to get the most recent ones
        const allSortedTransactions = sortTransactionsChronologically([...allTransactions]);
        const limitedTransactions = allSortedTransactions.slice(0, limit);
        
        // Now separate the limited transactions back into their categories for grouped mode
        const limitedBalanceOps = filterBalanceOperations(limitedTransactions);
        const limitedTokenTransactions = filterTokenTransactions(limitedTransactions);
        
        // Separate limited token transactions back into pending/completed based on original status
        const limitedPendingTokens = limitedTokenTransactions.filter(tx => {
          const txId = tx.id || tx.key || tx._id;
          return pendingTransactions.some(pt => (pt.id || pt.key || pt._id) === txId);
        });
        const limitedCompletedTokens = limitedTokenTransactions.filter(tx => {
          const txId = tx.id || tx.key || tx._id;
          return completedTransactions.some(ct => (ct.id || ct.key || ct._id) === txId);
        });
        
        // Set the limited transactions for both modes
        setTransactions(limitedTransactions); // For chronological mode - shows all 20 sorted
        setPendingTransactions(limitedPendingTokens); // For grouped mode - shows only those 20 that are pending
        setCompletedTransactions(limitedCompletedTokens); // For grouped mode - shows only those 20 that are completed
        setBalanceOperations(limitedBalanceOps); // For grouped mode - shows only those 20 that are balance ops
        
        console.info(`📊 RecentActivity: Showing ${limitedTransactions.length} most recent transactions (${limitedPendingTokens.length} pending, ${limitedCompletedTokens.length} completed, ${limitedBalanceOps.length} balance ops)`);
      } else {
        setPendingTransactions(pendingTokens);
        setCompletedTransactions(completedTokens);
        setBalanceOperations(sortTransactionsChronologically(balanceOps));
        // Store combined list for UI rendering (for chronological mode)
        setTransactions(tokenTransactions);
      }
      
      console.info(`📊 Final counts: ${pendingTokens.length} active tokens, ${completedTokens.length} completed tokens, ${balanceOps.length} balance operations`);
      if (codeFilter) {
        setTotalPages(1);
        setTotalTransactions(tokenTransactions.length);
      } else {
        setTotalPages(response.totalPages);
        setTotalTransactions(response.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleFilterChange = (newFilters: TransactionFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    setSelectedTransaction(null); // Close any open details
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
    setSelectedTransaction(null); // Close any open details
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      fetchTransactions();
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedTransaction(null); // Close any open details when changing pages
  };

  const handleTransactionSelect = (transaction: Transaction) => {
    // Use the correct property for comparison - transactions have 'key' not 'id'
    const transactionId = transaction.id || transaction.key || transaction._id;
    const selectedId = selectedTransaction?.id || selectedTransaction?.key || selectedTransaction?._id;
    
    // Toggle: if the same transaction is clicked, close it; otherwise, open the new one
    if (selectedTransaction && selectedId === transactionId) {
      setSelectedTransaction(null);
    } else {
      setSelectedTransaction(transaction);
    }
  };

  const handleCloseDetails = () => {
    setSelectedTransaction(null);
    if (autoOpenDetails && codeFilter) {
      navigate(`/transactions/${encodeURIComponent(codeFilter)}`, { replace: true });
    }
  };

  const handleRefresh = () => {
    fetchTransactions(true);
  };

  const handleExport = async (format: 'csv' | 'pdf', exportFilters?: any) => {
    try {
      const result = await platformApi.exportTransactions(format, {
        ...filters,
        ...exportFilters,
      });
      
      // Download the file
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = `transactions.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Export successful',
        description: `Transactions exported as ${format.toUpperCase()}`,
      });
    } catch (err: any) {
      toast({
        title: 'Export failed',
        description: err.message || 'Failed to export transactions',
        variant: 'destructive',
      });
    }
  };

  if (!user) return null;

  if (embedded) {
    // Embedded mode: just show the transaction list
    return (
      <TransactionListContainer
        transactions={transactions}
        pendingTransactions={pendingTransactions}
        completedTransactions={completedTransactions}
        balanceOperations={balanceOperations}
        loading={loading}
        error={error}
        onRetry={() => fetchTransactions()}
        onTransactionSelect={handleTransactionSelect}
        selectedTransaction={selectedTransaction}
        onCloseDetails={handleCloseDetails}
        currentPage={showPagination ? currentPage : undefined}
        totalPages={showPagination ? totalPages : undefined}
        onPageChange={showPagination ? handlePageChange : undefined}
        showPagination={showPagination}
        emptyMessage="No recent activity."
        onSeeAll={onSeeAll}
        showSeparateSections={shouldShowSeparateSections(sortMode, pendingTransactions, completedTransactions)}
        sortMode={sortMode}
        loadingPadding="p-4"
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center space-x-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="p-2"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{i18n.__('transactionHistory.title')}</h1>
              <p className="text-sm text-muted-foreground">
                {i18n.__(totalTransactions === 1 ? 'transactionHistory.count' : 'transactionHistory.count_plural', { count: totalTransactions })}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {i18n.__('transactionHistory.refresh')}
            </Button>
            {!hideFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                {i18n.__('transactionHistory.filters')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowExport(true)}
            >
              <Download className="w-4 h-4 mr-2" />
              {i18n.__('transactionHistory.export')}
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        {!hideFilters && (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder={i18n.__('transactionHistory.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
                readOnly={Boolean(codeFilter)}
              />
            </div>
            {codeFilter && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-200 px-3 py-1 text-sm text-slate-700">
                  Code: {codeFilter}
                  <button
                    type="button"
                    onClick={() => navigate('/transactions')}
                    className="rounded-full p-0.5 hover:bg-slate-300"
                    aria-label="Clear code filter"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/transactions')}
                >
                  Clear filter
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        {!hideFilters && showFilters && (
          <Card>
            <CardHeader>
              <CardTitle>{i18n.__('transactionHistory.filters.title')}</CardTitle>
              <CardDescription>
                {i18n.__('transactionHistory.filters.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TransactionFilters
                filters={filters}
                onFiltersChange={handleFilterChange}
              />
            </CardContent>
          </Card>
        )}

        {/* Transaction List with Inline Details */}
        <Card>
          <CardContent className="p-0">
            <TransactionListContainer
              transactions={transactions}
              pendingTransactions={pendingTransactions}
              completedTransactions={completedTransactions}
              balanceOperations={balanceOperations}
              loading={loading}
              error={error}
              onRetry={() => fetchTransactions()}
              onTransactionSelect={handleTransactionSelect}
              selectedTransaction={selectedTransaction}
              onCloseDetails={handleCloseDetails}
              currentPage={showPagination ? currentPage : undefined}
              totalPages={showPagination ? totalPages : undefined}
              onPageChange={showPagination ? handlePageChange : undefined}
              showPagination={showPagination}
              showSeparateSections={shouldShowSeparateSections(sortMode, pendingTransactions, completedTransactions)}
              sortMode={sortMode}
              loadingPadding="p-8"
              emptyMessage={
                codeFilter
                  ? `No transactions found for code "${codeFilter}".`
                  : undefined
              }
            />
          </CardContent>
        </Card>

        {/* Export Dialog */}
        {showExport && (
          <TransactionExport
            isOpen={showExport}
            onClose={() => setShowExport(false)}
            onExport={handleExport}
            filters={filters}
          />
        )}
      </div>
    </div>
  );
};

export default TransactionHistoryPage; 